<template>
  <el-form-item
    v-if="fieldInfo && fieldInfo.isShow"
    :label="label || (fieldInfo && fieldInfo.zhName) || ''"
    :prop="prop"
    :rules="computedRules"
    :label-width="labelWidth || ''"
  >
    <!-- 输入框 -->
    <el-input
      v-if="type === 'input'"
      v-model="inputValue"
      :placeholder="placeholder"
      :size="size"
      :disabled="disabled"
      :maxlength="fieldInfo && fieldInfo.maxLength"
      :show-word-limit="showWordLimit"
      :clearable="clearable"
      class="form-input"
      v-bind="$attrs"
      v-on="$listeners"
    >
      <template v-if="fieldInfo && fieldInfo.unit" #append>{{ fieldInfo && fieldInfo.unit }}</template>
    </el-input>

    <!-- 文本框 -->
    <el-input
      v-else-if="type === 'textarea'"
      v-model="inputValue"
      type="textarea"
      :placeholder="placeholder"
      :size="size"
      :disabled="disabled"
      :rows="rows"
      :maxlength="fieldInfo && fieldInfo.maxLength"
      :show-word-limit="showWordLimit"
      :autosize="autosize"
      class="form-textarea"
      v-bind="$attrs"
      v-on="$listeners"
    />

    <!-- 下拉框（单选） -->
    <el-select
      v-else-if="type === 'select'"
      v-model="inputValue"
      :size="size"
      :disabled="disabled"
      :clearable="clearable"
      :filterable="filterable"
      :remote="remote"
      :remote-method="remoteMethod"
      :loading="loading"
      class="form-select"
      v-bind="$attrs"
      v-on="$listeners"
    >
      <el-option
        v-for="item in options"
        :key="item.value"
        :label="item.label"
        :value="item.value"
        :disabled="item.disabled"
      ></el-option>
    </el-select>

    <!-- 下拉框（多选） -->
    <el-select
      v-else-if="type === 'multiple-select'"
      v-model="inputValue"
      :size="size"
      :disabled="disabled"
      :clearable="clearable"
      :filterable="filterable"
      :remote="remote"
      :remote-method="remoteMethod"
      :loading="loading"
      multiple
      :multiple-limit="multipleLimit"
      collapse-tags
      class="form-select"
      v-bind="$attrs"
      v-on="$listeners"
    >
      <el-option
        v-for="item in options"
        :key="item.value"
        :label="item.label"
        :value="item.value"
        :disabled="item.disabled"
      ></el-option>
    </el-select>

    <!-- 级联选择器 -->
    <el-cascader
      v-else-if="type === 'cascader'"
      v-model="inputValue"
      :options="cascaderOptions"
      :props="cascaderProps"
      :size="size"
      :disabled="disabled"
      :clearable="clearable"
      :filterable="filterable"
      :show-all-levels="showAllLevels"
      class="form-cascader"
      v-bind="$attrs"
      v-on="$listeners"
    />

    <!-- 日期选择器 -->
    <el-date-picker
      v-else-if="type === 'date'"
      v-model="inputValue"
      type="date"
      placeholder="请选择日期"
      :size="size"
      :disabled="disabled"
      :clearable="clearable"
      :format="dateFormat"
      :value-format="dateFormat"
      :picker-options="pickerOptions"
      class="form-date-picker"
      v-bind="$attrs"
      v-on="$listeners"
    />

    <!-- 日期范围选择器 -->
    <el-date-picker
      v-else-if="type === 'date-range'"
      v-model="inputValue"
      type="daterange"
      :start-placeholder="startPlaceholder"
      :end-placeholder="endPlaceholder"
      :size="size"
      :disabled="disabled"
      :clearable="clearable"
      :format="dateRangeFormat"
      :value-format="dateRangeFormat"
      :range-separator="rangeSeparator"
      :picker-options="pickerOptions"
      class="form-date-range-picker"
      v-bind="$attrs"
      v-on="$listeners"
    />
  </el-form-item>
</template>

<script>
import utils from '@/views/DoctorVisitLTC/utils/index.js'
export default {
  name: 'CustomFormItem',
  props: {
    // 表单字段类型: input | textarea | select | multiple-select | cascader | date | date-range
    type: {
      type: String,
      default: 'input',
      validator: (val) => ['input', 'textarea', 'select', 'multiple-select', 'cascader', 'date', 'date-range'].includes(val)
    },
    label: {
      type: String,
      default: ''
    },
    // 表单项值
    value: {
      type: [String, Number, Array],
      default: ''
    },
    // 标签宽度
    labelWidth: {
      type: String,
      default: ''
    },
    // 表单属性名
    prop: {
      type: String,
      default: ''
    },
    // 占位符
    placeholder: {
      type: String,
      default: '请填写'
    },
    // 尺寸: medium | small | mini
    size: {
      type: String,
      default: 'small'
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    // 是否显示字数统计
    showWordLimit: {
      type: Boolean,
      default: false
    },
    // 是否可清空
    clearable: {
      type: Boolean,
      default: true
    },
    // textarea行数
    rows: {
      type: Number,
      default: 3
    },
    // textarea自适应
    autosize: {
      type: [Boolean, Object],
      default: false
    },
    // 是否可搜索
    filterable: {
      type: Boolean,
      default: false
    },
    // 是否为远程搜索
    remote: {
      type: Boolean,
      default: false
    },
    // 远程搜索方法
    remoteMethod: {
      type: Function,
      default: null
    },
    // 远程搜索加载状态
    loading: {
      type: Boolean,
      default: false
    },
    // 下拉框选项
    options: {
      type: Array,
      default: () => []
    },
    // 多选限制
    multipleLimit: {
      type: Number,
      default: 999999
    },
    // 级联选择器选项
    cascaderOptions: {
      type: Array,
      default: () => []
    },
    // 级联选择器配置
    cascaderProps: {
      type: Object,
      default: () => ({})
    },
    // 级联选择器是否显示所有层级
    showAllLevels: {
      type: Boolean,
      default: true
    },
    // 触发方式: change
    trigger: {
      type: String,
      default: 'change'
    },
    // 字段信息对象
    fieldInfo: {
      type: Object,
      default: () => ({})
    },
    // 日期选择器格式
    dateFormat: {
      type: String,
      default: 'yyyy-MM-dd'
    },
    // 日期范围选择器格式
    dateRangeFormat: {
      type: String,
      default: 'yyyy-MM-dd'
    },
    // 日期范围开始占位符
    startPlaceholder: {
      type: String,
      default: '开始日期'
    },
    // 日期范围结束占位符
    endPlaceholder: {
      type: String,
      default: '结束日期'
    },
    // 日期范围分隔符
    rangeSeparator: {
      type: String,
      default: '至'
    },
    // 日期选择器配置项
    pickerOptions: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    // 计算属性: 输入值双向绑定
    inputValue: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    // 计算属性: 表单校验规则
    computedRules() {
      const rules = []
      const fieldInfo = this.fieldInfo || {}

      // 必填验证
      const isRequired = fieldInfo.required === true || fieldInfo.required === 1 || fieldInfo.required === '1' || fieldInfo.required === 'true'
      if (isRequired) {
        rules.push({
          required: true,
          message: fieldInfo.requiredMsg || '请填写',
          trigger: this.trigger || 'change'
        })
      }

      // 只对 input 类型应用以下验证
      if (this.type === 'input') {
        // 最小值验证
        if (!utils.isEmpty(fieldInfo.minValue)) {
          rules.push({
            validator: (rule, value, callback) => {
              if (utils.isEmpty(value)) {
                callback()
                return
              }
              const numValue = Number(value)
              if (isNaN(numValue)) {
                callback(new Error('请输入有效的数字'))
              } else if (numValue < fieldInfo.minValue) {
                callback(new Error(`输入值不能小于${fieldInfo.minValue}`))
              } else {
                callback()
              }
            },
            trigger: this.trigger || 'change'
          })
        }

        // 最大值验证
        if (!utils.isEmpty(fieldInfo.maxValue)) {
          rules.push({
            validator: (rule, value, callback) => {
              if (utils.isEmpty(value)) {
                callback()
                return
              }
              const numValue = Number(value)
              if (isNaN(numValue)) {
                callback(new Error('请输入有效的数字'))
              } else if (numValue > fieldInfo.maxValue) {
                callback(new Error(`输入值不能大于${fieldInfo.maxValue}`))
              } else {
                callback()
              }
            },
            trigger: this.trigger || 'change'
          })
        }

        // 正则表达式验证
        if (!utils.isEmpty(fieldInfo.regex)) {
          rules.push({
            validator: (rule, value, callback) => {
              if (utils.isEmpty(value)) {
                callback()
                return
              }
              const regex = new RegExp(fieldInfo.regex)
              if (regex.test(value)) {
                callback()
              } else {
                callback(new Error(fieldInfo.regexMsg || '输入格式不正确'))
              }
            },
            trigger: this.trigger || 'change'
          })
        }

        // 最小长度验证
        if (!utils.isEmpty(fieldInfo.minLength)) {
          rules.push({
            validator: (rule, value, callback) => {
              if (utils.isEmpty(value)) {
                callback()
                return
              }
              const strValue = String(value)
              if (strValue.length < fieldInfo.minLength) {
                callback(new Error(`输入内容长度不能少于指定长度（最少${fieldInfo.minLength}个字符）`))
              } else {
                callback()
              }
            },
            trigger: this.trigger || 'change'
          })
        }

        // 最大长度验证
        if (!utils.isEmpty(fieldInfo.maxLength)) {
          rules.push({
            validator: (rule, value, callback) => {
              if (utils.isEmpty(value)) {
                callback()
                return
              }
              const strValue = String(value)
              if (strValue.length > fieldInfo.maxLength) {
                callback(new Error(`输入内容长度不能超过指定长度（最多${fieldInfo.maxLength}个字符）`))
              } else {
                callback()
              }
            },
            trigger: this.trigger || 'change'
          })
        }
      }

      // 日期验证（适用于 date 和 date-range 类型）
      if (this.type === 'date') {
        // 最小日期验证
        if (!utils.isEmpty(fieldInfo.minDate)) {
          rules.push({
            validator: (rule, value, callback) => {
              if (utils.isEmpty(value)) {
                callback()
                return
              }
              if (value < fieldInfo.minDate) {
                callback(new Error(`日期不能早于${fieldInfo.minDate}`))
              } else {
                callback()
              }
            },
            trigger: this.trigger || 'change'
          })
        }

        // 最大日期验证
        if (!utils.isEmpty(fieldInfo.maxDate)) {
          rules.push({
            validator: (rule, value, callback) => {
              if (utils.isEmpty(value)) {
                callback()
                return
              }
              if (value > fieldInfo.maxDate) {
                callback(new Error(`日期不能晚于${fieldInfo.maxDate}`))
              } else {
                callback()
              }
            },
            trigger: this.trigger || 'change'
          })
        }
      }

      // 日期范围验证
      if (this.type === 'date-range') {
        // 验证日期范围的有效性
        rules.push({
          validator: (rule, value, callback) => {
            if (utils.isEmpty(value)) {
              callback()
              return
            }
            if (Array.isArray(value) && value.length === 2) {
              if (value[0] && value[1] && value[0] > value[1]) {
                callback(new Error('开始日期不能晚于结束日期'))
              } else {
                callback()
              }
            } else {
              callback()
            }
          },
          trigger: this.trigger || 'change'
        })
      }

      return rules
    }
  }
}
</script>

<style scoped lang="less">
.form-input,
.form-textarea,
.form-select,
.form-cascader,
.form-date-picker,
.form-date-range-picker {
  width: 100%;
}

// 级联选择器宽度
.form-cascader {
  ::v-deep .el-input__inner {
    width: 100%;
  }
}

// 日期范围选择器宽度
.form-date-range-picker {
  width: 100%;
}
</style>
